import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../services/api';
import LMSHeader from '../components/LMSHeader';
import { useToast } from '../components/ToastContainer';

const VerifyCertificatePage = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [certificateId, setCertificateId] = useState('');
    const [certificate, setCertificate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!certificateId.trim()) {
            return toast.error('Please enter a certificate ID');
        }

        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosClient.get(`/certificates/verify/${certificateId}`);
            console.log(res)
            setCertificate(res.data.certificate);
        } catch (err) {
            setError(err.response?.data?.error || 'Certificate not found');
            setCertificate(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewCertificate = () => {
        if (certificate) {
            navigate(`/verify/${certificate?.certificateId}`);
        }
    };

    return (
        <>
            <LMSHeader />
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                <div className="max-w-2xl mx-auto px-4">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Verify Certificate</h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                            Enter a certificate ID to verify its authenticity
                        </p>
                    </div>

                    {/* Search Form */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
                        <form onSubmit={handleSearch} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Certificate ID
                                </label>
                                <div className="flex items-center space-x-2">
                                    <span className="text-neutral-800 dark:text-neutral-200 font-medium">CERT-</span>
                                    <input
                                        type="text"
                                        value={certificateId}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase();
                                            setCertificateId(`${value}`);
                                        }}
                                        placeholder="Enter last 6 characters..."
                                        className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 uppercase"
                                        maxLength={6}
                                        required
                                    />
                                </div>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                    Enter the last <span className="font-semibold">6 alphanumeric characters</span> of your certificate ID.
                                </p>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                            >
                                {isLoading ? 'Verifying...' : 'Verify Certificate'}
                            </button>
                        </form>
                    </div>

                    {certificate && (
                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Certificate Verified</h2>
                                <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                    This certificate is authentic and was issued by BoleumLearn.
                                </p>
                                <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 mb-6">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-neutral-500 dark:text-neutral-400">Student</p>
                                            <p className="font-medium text-neutral-900 dark:text-white">{certificate?.student?.name}</p>
                                        </div>
                                        <div>
                                            <p className="text-neutral-500 dark:text-neutral-400">Course</p>
                                            <p className="font-medium text-neutral-900 dark:text-white">{certificate?.course?.title}</p>
                                        </div>
                                        <div>
                                            <p className="text-neutral-500 dark:text-neutral-400">Issue Date</p>
                                            <p className="font-medium text-neutral-900 dark:text-white">
                                                {new Date(certificate?.issueDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-neutral-500 dark:text-neutral-400">Status</p>
                                            <span
                                                className={`px-2 py-1 text-xs font-medium rounded-full ${certificate?.status === 'issued'
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                    : certificate?.status === 'pending'
                                                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                                        : certificate?.status === 'revoked'
                                                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                            : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
                                                    }`}
                                            >
                                                {certificate?.status
                                                    ? certificate.status.charAt(0).toUpperCase() + certificate.status.slice(1)
                                                    : 'Unknown'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleViewCertificate}
                                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    View Certificate
                                </button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Verification Failed</h2>
                                <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                    {error}
                                </p>
                                <button
                                    onClick={() => setCertificateId('')}
                                    className="px-6 py-3 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 font-medium rounded-lg shadow-sm"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default VerifyCertificatePage;